import type {
  DuplicateAttachmentFile,
  OversizedFile,
} from '@snap-types/actions/send-snap-attachments';
import { z, ZodError } from 'zod/v4';

import { ERROR_CODES } from '../constants';
import type {
  ApiErrorResponse,
  ApplicationErrorOpts,
  ErrorCode,
} from '../types';

/**
 * Base class for application errors.
 *
 * @remarks
 *
 * Defaults to status code 500 (Internal Server Error) with a user facing
 * message of "An unexpected error occurred.".
 *
 * When serializing to a response, the detail field will include both the user
 * message and the error code.
 *
 * If the `cause` is a ZodError, treeified `validationErrors` will be included
 * in the response.
 */
export class ApplicationError<InnerError extends Error = Error> extends Error {
  readonly code: ErrorCode;
  readonly statusCode: number;
  readonly explanation?: string;
  readonly reportToSentry: boolean;
  readonly metadata?: Record<string, unknown>;
  readonly cause?: unknown;
  readonly innerErrors?: InnerError[];
  readonly oversizedFiles?: OversizedFile[];
  readonly fileSizeLimitInBytes?: number;
  readonly duplicateFiles?: DuplicateAttachmentFile[];

  constructor(
    message: string,
    defaultStatusCode: number = 500,
    options: ApplicationErrorOpts<InnerError> = {},
  ) {
    super(message);

    this.name = this.constructor.name;
    this.statusCode = options.statusCode ?? defaultStatusCode;
    this.explanation = options.explanation;
    this.reportToSentry =
      options.reportToSentry ??
      (this.statusCode >= 500 || this.statusCode < 400);
    this.code = options.code ?? ERROR_CODES.SNAP_UNKNOWN_ERROR;
    this.metadata = options.metadata;
    this.innerErrors = options.innerErrors;
    this.cause = options.cause;

    /**
     * Maintains proper prototype chain for instanceof checks
     *
     * @see {@link https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work}
     */
    Object.setPrototypeOf(this, new.target.prototype);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  toResponse(): ApiErrorResponse {
    return {
      statusCode: this.statusCode,
      code: this.code,
      error: this.name,
      message: this.message,
      detail: this.withErrorCode(
        this.explanation ||
          (this.cause instanceof Error ? this.cause.message : undefined) ||
          'An unexpected error occurred.',
      ),
      validationErrors:
        this.cause instanceof ZodError ? z.treeifyError(this.cause) : undefined,
      oversizedFiles: this.oversizedFiles,
      fileSizeLimitInBytes: this.fileSizeLimitInBytes,
      duplicateFiles: this.duplicateFiles,
      metadata: this.metadata,
    };
  }

  withErrorCode(text: string): string {
    return `${text} (E${this.code})`;
  }
}

export class ClientError extends ApplicationError {
  constructor(message: string, options?: ApplicationErrorOpts) {
    super(message, 400, options);
  }
}

export class UnauthorizedError extends ApplicationError {
  constructor(
    message: string = 'Unauthorized',
    options?: ApplicationErrorOpts,
  ) {
    super(message, 401, options);
  }
}

export class ForbiddenError extends ApplicationError {
  constructor(message: string = 'Forbidden', options?: ApplicationErrorOpts) {
    super(message, 403, options);
  }
}

export class NotFoundError extends ApplicationError {
  constructor(message: string = 'Not Found', options?: ApplicationErrorOpts) {
    super(message, 404, options);
  }
}

export class ConflictError extends ApplicationError {
  constructor(message: string, options?: ApplicationErrorOpts) {
    super(message, 409, options);
  }
}

export class UnprocessableEntityError extends ApplicationError {
  constructor(message: string, options?: ApplicationErrorOpts) {
    super(message, 422, options);
  }
}

export class OversizedFilesError extends ApplicationError {
  constructor(
    message: string,
    public oversizedFiles: OversizedFile[],
    public fileSizeLimitInBytes: number,
    options?: Omit<ApplicationErrorOpts, 'code'>,
  ) {
    super(message, 422, {
      reportToSentry: true,
      metadata: {
        ...options?.metadata,
        code: ERROR_CODES.SNAP_SEND_QUOTE_OVERSIZED_FILE_ERROR,
      },
      ...options,
    });
    this.oversizedFiles = oversizedFiles;
    this.fileSizeLimitInBytes = fileSizeLimitInBytes;
  }
}

export class DuplicateAttachmentFilenamesError extends ApplicationError {
  constructor(
    message: string,
    public duplicateFiles: DuplicateAttachmentFile[],
    options?: Omit<ApplicationErrorOpts, 'code'>,
  ) {
    super(message, 422, {
      code: ERROR_CODES.SNAP_SEND_QUOTE_DUPLICATE_FILENAME_ERROR,
      explanation:
        options?.explanation ??
        'Attachment names must be unique. Rename the file and try again.',
      ...options,
    });
    this.duplicateFiles = duplicateFiles;
  }
}
export class UnexpectedError extends ApplicationError {
  constructor(message: string, options?: ApplicationErrorOpts) {
    super(message, 500, { reportToSentry: true, ...options });
  }
}
